import React from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchPost } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    Tray,
    PanelWithoutForm,
    PanelBody,
    Table,
    TableHead,
    TableHeadRow,
    TableHeader,
    EditPanelInput,
    EditPanelSection,
    TableBody,
    TableData,
    TableRow,
    TableButtons,
} from '../../elements';
import { RightSearchTray } from '../../utils/overview/RightSearchTray';
import { ClientCompanyDependentPage, clientCompanyIdContext } from '../ClientCompanyDependentPage';

export function SurveyLabelsList() {
    const translate = useTranslate();

    return (
        <ClientCompanyDependentPage topBarIcon="fa-tag" topBarTitle={translate('survey.labels')}>
            <clientCompanyIdContext.Consumer>
                {(clientCompanyId) => <SurveyLabelsListContents clientCompanyId={clientCompanyId} />}
            </clientCompanyIdContext.Consumer>
        </ClientCompanyDependentPage>
    );
}

export function EditableLabelRow({ label, clientCompanyId, reload }: { label: string; clientCompanyId: string; reload: () => void }) {
    const translate = useTranslate();

    const [newLabel, setNewLabel] = React.useState<string>('');
    const [isInEditMode, setIsInEditMode] = React.useState<boolean>(false);

    function setLabelInEditMode() {
        if (isInEditMode) {
            setIsInEditMode(false);
        } else {
            setIsInEditMode(true);
            setNewLabel(label);
        }
    }
    async function updateLabel() {
        if (newLabel) {
            await openApiFetchPost('/api/surveys/labels/editable', { clientCompanyId }, { oldLabel: label, newLabel });
            setIsInEditMode(false);
            reload();
        }
    }
    function cancelUpdateLabel() {
        setIsInEditMode(false);
    }

    async function removeLabel() {
        await openApiFetchDelete('/api/surveys/labels/editable', { clientCompanyId, label });
        reload();
    }

    return (
        <TableRow key={label}>
            <TableData>
                {isInEditMode ? (
                    <EditPanelInput
                        name="label"
                        value={newLabel}
                        handleChange={(value) => setNewLabel(value)}
                        handleBlur={updateLabel}
                        handleEsc={cancelUpdateLabel}
                        handleEnter={updateLabel}
                        autoFocus
                    />
                ) : (
                    <>{label}</>
                )}
            </TableData>
            <TableButtons>
                <IconEditButton onClick={() => setLabelInEditMode()} />
                <IconRemoveButton removeQuestion={translate('common.confirmDelete', label)} onClick={removeLabel} />
            </TableButtons>
        </TableRow>
    );
}

export function SurveyLabelsListContents({ clientCompanyId }: { clientCompanyId: string }) {
    const translate = useTranslate();

    const [searchText, setSearchText] = React.useState('');

    const { data, reload } = useOpenApiFetch('/api/surveys/labels/editable', 'get', { searchText, clientCompanyId }, [searchText, clientCompanyId]);

    return (
        <>
            <Tray>
                <PanelWithoutForm>
                    <PanelBody noPadding={true}>
                        <Table hover={true}>
                            <TableHead>
                                <TableHeadRow light={true}>
                                    <TableHeader>{translate('common.label')}</TableHeader>
                                    <TableHeader align="right">&nbsp; </TableHeader>
                                </TableHeadRow>
                            </TableHead>
                            <TableBody>
                                {data?.items.map((label) => (
                                    <EditableLabelRow key={label} label={label} clientCompanyId={clientCompanyId} reload={reload} />
                                ))}
                            </TableBody>
                        </Table>
                    </PanelBody>
                </PanelWithoutForm>
            </Tray>
            <RightSearchTray header={translate('label.searchLabels')}>
                <EditPanelSection>
                    <EditPanelInput
                        name="searchText"
                        placeholder={translate('label.labelText')}
                        value={searchText}
                        icon="fa fa-building"
                        handleChange={(value) => setSearchText(value)}
                    />
                </EditPanelSection>
            </RightSearchTray>
        </>
    );
}
